/**
 * Mobile bottom fade (max-width 767px). Markup and styles: snippets/floating-dock-fade.liquid.
 *
 * Sizes the frosted fade that sits behind the floating row (cart pill, Feedback, Zendesk Chat):
 * - Its top is 12px above the highest of those that is showing, so it follows the row when
 *   Chat moves up above the PDP sticky buy bar.
 * - It reads the background colour under the row and picks the matching tint (white over light
 *   content, near-black over dark content), with a little hysteresis so it does not flicker at
 *   a boundary.
 * - Its bottom is the screen edge, or the top of any other bar pinned to the bottom (sticky buy
 *   bar, cookie banner), so those never get frosted over.
 * - It hides when nothing is floating there, and while a full-screen layer (drawer, popup,
 *   chat window) is open.
 * It never captures taps and does not change any other element.
 */
(() => {
  'use strict';

  if (window.__aetrexDockFadeLoaded) return;
  window.__aetrexDockFadeLoaded = true;

  const fade = document.getElementById('AetrexDockFade');
  if (!fade) return;

  const root = document.documentElement;
  const MOBILE = window.matchMedia('(max-width: 767px)');
  const ZENDESK = 'iframe#launcher, iframe[title="Button to launch messaging window"]';
  const FEEDBACK = '.aetrex-tf-cta.is-visible';
  const PILL = '#AetrexCartPill.is-visible';
  const FADE_ABOVE = 12;
  const MIN_HEIGHT = 30;
  const TICK_MS = 250;

  let visible = false;
  let timer = 0;
  let current = { bottom: -1, height: -1 };
  let dark = false;

  const setVisible = (next) => {
    if (next === visible) return;
    visible = next;
    // Make sure the browser has computed the layers at opacity 0 before the class changes,
    // otherwise a first show that lands before the first paint skips the transition.
    if (next) void fade.offsetHeight;
    fade.classList.toggle('is-visible', next);
  };

  const isMember = (el) =>
    fade.contains(el) ||
    !!(el.closest && (el.closest('.aetrex-tf-cta') || el.closest('#AetrexCartPill'))) ||
    !!(el.matches && el.matches(ZENDESK));

  // Zendesk's launcher iframe is larger than the Chat pill inside it: measure the pill when the
  // frame allows it, otherwise the frame.
  const launcherRect = (frame) => {
    const outer = frame.getBoundingClientRect();
    try {
      const doc = frame.contentDocument;
      const button = doc && (doc.querySelector('[data-testid="launcher"]') || doc.querySelector('button'));
      if (button) {
        const inner = button.getBoundingClientRect();
        if (inner.width > 10 && inner.height > 10) {
          return { top: outer.top + inner.top, bottom: outer.top + inner.bottom, left: outer.left + inner.left, right: outer.left + inner.right };
        }
      }
    } catch (error) {
      // Cross-origin frame: use its box.
    }
    return { top: outer.top, bottom: outer.bottom, left: outer.left, right: outer.right };
  };

  const rowTop = (vw, vh) => {
    let top = Infinity;
    const consider = (r) => {
      if (r.bottom - r.top < 10 || r.right - r.left < 10) return;
      if (r.bottom <= 0 || r.top >= vh || r.right <= 0 || r.left >= vw) return;
      top = Math.min(top, r.top);
    };
    document.querySelectorAll(ZENDESK).forEach((frame) => {
      const style = getComputedStyle(frame);
      if (style.visibility === 'hidden' || style.display === 'none' || parseFloat(style.opacity) === 0) return;
      consider(launcherRect(frame));
    });
    [FEEDBACK, PILL].forEach((selector) => {
      const el = document.querySelector(selector);
      if (el) consider(el.getBoundingClientRect());
    });
    return top;
  };

  // The fixed (or bottom-sticky) box an element belongs to, if any.
  const pinnedBox = (el, memo) => {
    if (!el || el === root || el === document.body) return null;
    if (memo.has(el)) return memo.get(el);
    let box = null;
    if (!isMember(el)) {
      const style = getComputedStyle(el);
      if (style.position === 'fixed' || (style.position === 'sticky' && style.bottom !== 'auto')) {
        box = parseFloat(style.opacity) < 0.05 ? null : el;
      } else {
        box = pinnedBox(el.parentElement, memo);
      }
    }
    memo.set(el, box);
    return box;
  };

  // Where the fade must stop: the top of the highest bar pinned to the bottom below the row.
  // Returns { overlay: true } when a full-screen layer covers the area.
  const floorAbove = (top, vw, vh) => {
    if (typeof document.elementsFromPoint !== 'function') return { floor: vh };
    const xs = [vw * 0.08, vw * 0.5, vw * 0.92];
    const ys = [vh - 6, vh - 40, Math.min(vh - 6, top + 24)];
    const memo = new Map();
    let floor = vh;
    for (const x of xs) {
      for (const y of ys) {
        if (y < 0 || y >= vh) continue;
        for (const el of document.elementsFromPoint(x, y)) {
          const box = pinnedBox(el, memo);
          if (!box) continue;
          const r = box.getBoundingClientRect();
          if (r.width >= vw * 0.6 && r.height >= vh * 0.4) return { overlay: true };
          if (r.top > top && r.top < floor) floor = r.top;
        }
      }
    }
    return { floor };
  };

  // Relative luminance (0..1) of a CSS colour string, or null when it is not a solid colour.
  const luminance = (color) => {
    const m = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(color || '');
    if (!m) return null;
    const alpha = m[4] === undefined ? 1 : parseFloat(m[4]);
    if (alpha < 0.5) return null;
    return (0.2126 * parseFloat(m[1]) + 0.7152 * parseFloat(m[2]) + 0.0722 * parseFloat(m[3])) / 255;
  };

  // Luminance of what is painted at a point, ignoring the floating row itself. null for
  // photos and video, where the colour cannot be known.
  const backgroundAt = (x, y) => {
    for (const el of document.elementsFromPoint(x, y)) {
      if (isMember(el)) continue;
      for (let node = el; node && node !== root; node = node.parentElement) {
        const tag = node.tagName;
        if (tag === 'IMG' || tag === 'VIDEO' || tag === 'CANVAS' || tag === 'PICTURE' || tag === 'SVG') return null;
        const style = getComputedStyle(node);
        if (style.backgroundImage && style.backgroundImage !== 'none') return null;
        const value = luminance(style.backgroundColor);
        if (value !== null) return value;
      }
      return luminance(getComputedStyle(document.body).backgroundColor) ?? 1;
    }
    return null;
  };

  // Dark tint when the content under the row is dark. Switches below 0.4 and back above 0.5.
  const updateTint = (top, floor, vw) => {
    if (typeof document.elementsFromPoint !== 'function') return;
    const ys = [top + 16, Math.max(top + 16, floor - 10)];
    const xs = [vw * 0.15, vw * 0.5, vw * 0.85];
    let sum = 0;
    let count = 0;
    for (const x of xs) {
      for (const y of ys) {
        const value = backgroundAt(x, y);
        if (value === null) continue;
        sum += value;
        count += 1;
      }
    }
    if (!count) return;
    const average = sum / count;
    const next = dark ? average <= 0.5 : average < 0.4;
    if (next !== dark) {
      dark = next;
      fade.classList.toggle('is-dark', dark);
    }
  };

  const apply = (bottom, height) => {
    if (Math.abs(bottom - current.bottom) >= 1) {
      fade.style.bottom = `${Math.round(bottom)}px`;
      current.bottom = bottom;
    }
    if (Math.abs(height - current.height) >= 1) {
      fade.style.height = `${Math.round(height)}px`;
      current.height = height;
    }
  };

  const tick = () => {
    if (!MOBILE.matches) {
      setVisible(false);
      return;
    }
    if (document.hidden) return;
    let show = false;
    try {
      const vw = root.clientWidth;
      const vh = window.innerHeight;
      const top = rowTop(vw, vh);
      if (top !== Infinity) {
        const result = floorAbove(top, vw, vh);
        if (!result.overlay) {
          const height = result.floor - (top - FADE_ABOVE);
          if (height >= MIN_HEIGHT) {
            apply(vh - result.floor, height);
            updateTint(top, result.floor, vw);
            show = true;
          }
        }
      }
    } catch (error) {
      show = false;
    }
    setVisible(show);
  };

  const start = () => {
    if (!timer) timer = setInterval(tick, TICK_MS);
    tick();
  };

  if (MOBILE.addEventListener) MOBILE.addEventListener('change', tick);
  else if (MOBILE.addListener) MOBILE.addListener(tick);
  window.addEventListener('resize', tick);
  window.addEventListener('pageshow', tick);
  start();
})();
